import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Grievance from "./components/support/Grievance.jsx";
import CustomerForm from "./components/customer/CustomerForm.jsx";
import TransactionHistory from "./components/transaction/TransactionHistory.jsx";
import TransactionDetails from "./components/transaction/TransactionDetails.jsx";
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from "@apollo/client";
import CheckYourQuery from "./components/queryStatus/CheckYourQuery.jsx";
import TicketForm from "./components/ticketing/TicketForm.jsx";
import TicketPage from "./components/ticketing/TicketPage.jsx";
import { CircularProgress } from "@mui/material";
import FAQsPage from "./pages/FAQsPage.jsx";
import { GRAPHQL_URL } from "./config/api";
import { BUSINESS_CONSTANTS } from "./config/businessConstants";
import { getAuthToken } from "./utils/auth";
import { getCsrfTokenSync, getCsrfTokenKeyByEnv } from "./utils/csrf";
import { AuthProvider, useAuth } from "./components/common/AuthContext.jsx";
import "./App.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const theme = createTheme();

// Custom hook for auto-scrolling to top on route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top when pathname changes
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Custom fetch function to handle file uploads
const customFetch = async (uri, options) => {
  const token = getAuthToken();
  const csrfToken = getCsrfTokenSync();
  const csrfTokenKey = getCsrfTokenKeyByEnv();

  // Check if this is a file upload request
  const hasFiles = options.body && options.body instanceof FormData;

  const finalHeaders = {
    'Apollo-Require-Preflight': 'true',
  };

  if (!hasFiles) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  if (token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  if (csrfToken) {
    finalHeaders[csrfTokenKey] = csrfToken;
  }

  const finalOptions = {
    ...options,
    headers: finalHeaders,
    credentials: 'include',
    mode: 'cors',
  };

  try {
    const response = await fetch(uri, finalOptions);

    // If we get a CSRF token invalid status, retry once with a fresh token from cookies
    if (response.status === BUSINESS_CONSTANTS.HTTP_STATUS_CODES.INVALID_CSRF) {
      console.log('CSRF token invalid, retrying request with fresh token');
      const freshCsrfToken = getCsrfTokenSync();
      if (freshCsrfToken) {
        finalOptions.headers[csrfTokenKey] = freshCsrfToken;
      }
      return await fetch(uri, finalOptions);
    }

    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Create HTTP link with custom fetch
const httpLink = createHttpLink({
  uri: GRAPHQL_URL,
  fetch: customFetch,
});

const client = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache({
    addTypename: false
  }),
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  console.log('ProtectedRoute: Auth state', { 
    isAuthenticated, 
    isLoading, 
    pathname: location.pathname,
    bypassAuth: location.state?.bypassAuth,
    changeEmail: location.state?.changeEmail
  });

  // Remove all logic related to paymentIdFlow from ProtectedRoute
  // Only keep authentication checks for the normal mobile number flow
  const isTransactionHistory = location.pathname === "/transaction-history";
  const isTransactionDetails = location.pathname.startsWith("/transaction-details/");
  const hasTransactionData = location.state && location.state.transactionData;

  if ((isTransactionHistory || isTransactionDetails) && (hasTransactionData)) {
    console.log('ProtectedRoute: Bypassing auth for transaction data');
    return children;
  }

  // Bypass auth if coming from CustomerForm's Track Existing Query or TicketForm with bypassAuth
  const isCheckQuery = location.pathname === "/check-query";
  const isTicketForm = location.pathname === "/ticket";
  const bypassAuth = location.state && location.state.bypassAuth;
  const isChangeEmail = location.state && location.state.changeEmail;
  
  if ((isCheckQuery || isTicketForm) && (bypassAuth || isChangeEmail)) {
    console.log('ProtectedRoute: Bypassing auth for bypassAuth or changeEmail');
    return children;
  }

  // Show loading spinner while checking authentication status
  if (isLoading) {
    console.log('ProtectedRoute: Showing loading spinner');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} color="primary" />
      </div>
    );
  }

  // If not authenticated and not loading, redirect to login
  if (!isAuthenticated) {
    console.log('ProtectedRoute: Not authenticated, redirecting to login');
    navigate('/', { replace: true });
    return null;
  }

  console.log('ProtectedRoute: Authenticated, rendering children');
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<CustomerForm />} />
      <Route path="/customer" element={<CustomerForm />} />
      <Route path="/faqs" element={<FAQsPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/transaction-history"
        element={
          <ProtectedRoute>
            <TransactionHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transaction-history/:phoneNumber"
        element={
          <ProtectedRoute>
            <TransactionHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transaction-details/:id"
        element={
          <ProtectedRoute>
            <TransactionDetails />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/check-query" 
        element={
          <ProtectedRoute>
            <CheckYourQuery />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ticket" 
        element={
          <ProtectedRoute>
            <TicketForm />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ticket-page" 
        element={
          <ProtectedRoute>
            <TicketPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

const App = () => {

  return (
    <AuthProvider>
      <ApolloProvider client={client}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <Router>
              <ScrollToTop />
              <ToastContainer position="bottom-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
              <AppRoutes />
            </Router>
          </ThemeProvider>
        </LocalizationProvider>
      </ApolloProvider>
    </AuthProvider>
  );
};

export default App;
